import { createSlice } from "@reduxjs/toolkit";
import type { ArticleSliceType } from "../../types/article/article.model";
import { axiosClient } from "../../configs/axiosClient";
import { API } from "../../configs/endpoints";
import { addAsyncCases, createApiThunk } from "../createApiThunk";
import type {
  CreateArticleResponse,
  GetArticleResponse,
  ListArticleResponse,
  UpdateArticleResponse,
} from "../../types/article/article.response";
import type {
  CreateArticlePayload,
  ListArticlePayload,
  UpdateArticlePayload,
} from "../../types/article/article.payload";

const initialState: ArticleSliceType = {
  loading: {
    list: false,
    detail: false,
    create: false,
    update: false,
    delete: false,
  },
  error: null,
  article: null,
  articles: [],
  pagination: null,
};

export const getArticles = createApiThunk<
  ListArticleResponse,
  ListArticlePayload
>(
  "article/getArticles",
  async (query) =>
    (await axiosClient.get(API.article.list, { params: query })).data,
  "Lấy danh sách bài viết thất bại",
);

export const createArticle = createApiThunk<
  CreateArticleResponse,
  CreateArticlePayload
>(
  "article/createArticle",
  async (data) => (await axiosClient.post(API.article.create, data)).data,
  "Tạo bài viết thất bại",
);

export const getArticle = createApiThunk<GetArticleResponse, string>(
  "article/getArticle",
  async (id) => (await axiosClient.get(API.article.detail(id))).data,
  "Lấy thông tin bài viết thất bại",
);

export const updateArticle = createApiThunk<
  UpdateArticleResponse,
  { id: string; data: UpdateArticlePayload }
>(
  "article/updateArticle",
  async ({ id, data }) =>
    (await axiosClient.put(API.article.update(id), data)).data,
  "Cập nhật bài viết thất bại",
);

export const deleteArticle = createApiThunk<string, string>(
  "article/deleteArticle",
  async (id) => {
    await axiosClient.delete(API.article.delete(id));
    return id;
  },
  "Xóa bài viết thất bại",
);

const articleSlice = createSlice({
  name: "article",
  initialState,
  reducers: {
    clearArticle: (state) => {
      state.article = null;
    },
  },
  extraReducers: (builder) => {
    addAsyncCases({
      builder,
      thunk: getArticles,
      loadingKey: "list",
      fallbackError: "Lấy danh sách bài viết thất bại",
      onSuccess: (state, payload) => {
        state.articles = payload.articles;
        state.pagination = payload.pagination;
      },
    });
    addAsyncCases({
      builder,
      thunk: createArticle,
      loadingKey: "create",
      fallbackError: "Tạo bài viết thất bại",
      onSuccess: (state, payload) => {
        state.articles.unshift(payload.article);
      },
    });
    addAsyncCases({
      builder,
      thunk: getArticle,
      loadingKey: "detail",
      fallbackError: "Lấy thông tin bài viết thất bại",
      onSuccess: (state, payload) => {
        state.article = payload.article;
      },
    });
    addAsyncCases({
      builder,
      thunk: updateArticle,
      loadingKey: "update",
      fallbackError: "Cập nhật bài viết thất bại",
      onSuccess: (state, payload) => {
        const updated = payload.article;
        const idx = state.articles.findIndex((a) => a._id === updated._id);
        if (idx !== -1) state.articles[idx] = updated;
      },
    });
    addAsyncCases({
      builder,
      thunk: deleteArticle,
      loadingKey: "delete",
      fallbackError: "Xóa bài viết thất bại",
      onSuccess: (state, payload) => {
        state.articles = state.articles.filter((a) => a._id !== payload);
      },
    });
  },
});

export const { clearArticle } = articleSlice.actions;
export default articleSlice.reducer;
